/**
 * Resupply Thresholds - PSP/CRE Tool v2.0
 * Phase 4.5: Configurable Severity Thresholds
 * 
 * Defines DOS threshold profiles by national standard for:
 * - Stockout warning severity levels
 * - MDOS status classification
 * - Resupply urgency determination
 * 
 * VERSION: 1.1.0 (March 1, 2026)
 *
 * v1.1.0 (March 1, 2026) — Phase G G-05 (ARCH-010 / C-012):
 * - Added ASSET_STATUS_THRESHOLDS constant for four-tier asset operational
 *   readiness classification. Consumed by Tab5 getAssetStatusColor() helper.
 *   Thresholds: GREEN ≥60%, YELLOW 40–59%, ORANGE 20–39%, RED <20%.
 *   Source: NATO medical planning norms for evacuation asset readiness.
 *   ARCH-010 context: CASEVAC tracking is informational per AJMedP-2;
 *   these tiers apply to MEDEVAC capacity utilization display in Tab5.
 *
 * VERSION: 1.0.0 (January 29, 2026)
 *
 * Author: Dr. Tatar Radu MD
 * Date: January 29, 2026
 */

// ============================================================================
// NATIONAL THRESHOLD PROFILES
// ============================================================================

/**
 * Severity thresholds by national standard
 * Values are in Days of Supply (DOS)
 */
export const THRESHOLD_PROFILES = {
  NATO: {
    id: 'NATO',
    label: 'NATO Standard',
    description: 'NATO medical doctrine thresholds (AJMedP-1)',
    
    // Stockout prediction severity
    stockout: {
      CRITICAL: 3,  // <3 days remaining = CRITICAL
      WARNING: 5,   // <5 days = WARNING
      CAUTION: 7    // <7 days = CAUTION
    },
    
    // MDOS status thresholds
    mdos: {
      GREEN: 15,    // 15+ days = Adequate
      BLUE: 10,     // 10-14 days = Monitor
      YELLOW: 5,    // 5-9 days = Plan resupply
      RED: 5        // <5 days = Critical shortage
    },
    
    // Resupply scheduling
    resupply: {
      emergency_threshold: 3,   // Schedule emergency if below this
      routine_threshold: 7,     // Schedule routine if below this
      target_dos: 15,           // Try to maintain this level
      safety_margin: 2          // Schedule N days before threshold
    }
  },
  
  ROMANIAN: {
    id: 'ROMANIAN',
    label: 'Romanian Standard',
    description: 'Romanian Army medical doctrine thresholds',
    
    // Romanian forces maintain higher safety margins
    stockout: {
      CRITICAL: 4,  // <4 days = CRITICAL
      WARNING: 6,   // <6 days = WARNING
      CAUTION: 8    // <8 days = CAUTION
    },
    
    mdos: {
      GREEN: 18,    // 18+ days = Adequate
      BLUE: 12,     // 12-17 days = Monitor
      YELLOW: 6,    // 6-11 days = Plan resupply
      RED: 6        // <6 days = Critical shortage
    },
    
    resupply: {
      emergency_threshold: 4,
      routine_threshold: 8,
      target_dos: 18,
      safety_margin: 3
    }
  },
  
  US: {
    id: 'US',
    label: 'US Standard',
    description: 'US Army medical doctrine thresholds (ATP 4-02)',
    
    // US doctrine more aggressive/tolerant of lower DOS
    stockout: {
      CRITICAL: 2,  // <2 days = CRITICAL
      WARNING: 4,   // <4 days = WARNING
      CAUTION: 6    // <6 days = CAUTION
    },
    
    mdos: {
      GREEN: 12,    // 12+ days = Adequate
      BLUE: 8,      // 8-11 days = Monitor
      YELLOW: 4,    // 4-7 days = Plan resupply
      RED: 4        // <4 days = Critical shortage
    },
    
    resupply: {
      emergency_threshold: 2,
      routine_threshold: 6,
      target_dos: 12,
      safety_margin: 1
    }
  }
};

// ============================================================================
// THRESHOLD FUNCTIONS
// ============================================================================

/**
 * Get threshold profile by national standard
 */
export function getThresholdProfile(nationalStandard = 'NATO') {
  return THRESHOLD_PROFILES[nationalStandard] || THRESHOLD_PROFILES.NATO;
}

/**
 * Determine stockout severity based on days remaining and national standard
 */
export function getStockoutSeverity(daysRemaining, nationalStandard = 'NATO') {
  const profile = getThresholdProfile(nationalStandard);
  
  if (daysRemaining < profile.stockout.CRITICAL) {
    return 'CRITICAL';
  } else if (daysRemaining < profile.stockout.WARNING) {
    return 'WARNING';
  } else if (daysRemaining < profile.stockout.CAUTION) {
    return 'CAUTION';
  } else {
    return 'ADEQUATE';
  }
}

/**
 * Determine MDOS status based on current DOS and national standard
 */
export function getMDOSStatus(currentDOS, nationalStandard = 'NATO') {
  const profile = getThresholdProfile(nationalStandard);
  
  if (currentDOS >= profile.mdos.GREEN) {
    return { status: 'GREEN', label: 'Adequate', color: 'success' };
  } else if (currentDOS >= profile.mdos.BLUE) {
    return { status: 'BLUE', label: 'Monitor', color: 'info' };
  } else if (currentDOS >= profile.mdos.YELLOW) {
    return { status: 'YELLOW', label: 'Plan Resupply', color: 'warning' };
  } else {
    return { status: 'RED', label: 'Critical', color: 'error' };
  }
}

/**
 * Determine if emergency resupply needed
 */
export function needsEmergencyResupply(daysRemaining, nationalStandard = 'NATO') {
  const profile = getThresholdProfile(nationalStandard);
  return daysRemaining < profile.resupply.emergency_threshold;
}

/**
 * Get recommended resupply day
 */
export function getRecommendedResupplyDay(stockoutDay, nationalStandard = 'NATO') {
  const profile = getThresholdProfile(nationalStandard);
  return Math.max(1, stockoutDay - profile.resupply.safety_margin);
}

// ============================================================================
// SUPPLY SOURCE DEFINITIONS
// ============================================================================

/**
 * Available supply sources with delivery characteristics
 */
export const SUPPLY_SOURCES = {
  ROLE_3: {
    id: 'ROLE_3',
    label: 'Role 3 (Corps Hospital)',
    description: 'Standard resupply from Corps-level CSH',
    delivery_time_hours: {
      BLUE_LIGHT: 2,    // Helicopter
      AIR: 4,           // Fixed-wing
      GROUND: 8         // Convoy
    },
    reliability: 0.95,  // 95% success rate
    capacity: 'UNLIMITED'
  },
  
  ROLE_4: {
    id: 'ROLE_4',
    label: 'Role 4 (Theater Hospital)',
    description: 'Strategic resupply from theater-level facility',
    delivery_time_hours: {
      BLUE_LIGHT: 6,
      AIR: 12,
      GROUND: 24
    },
    reliability: 0.98,
    capacity: 'UNLIMITED'
  },
  
  THEATER_DEPOT: {
    id: 'THEATER_DEPOT',
    label: 'Theater Supply Depot',
    description: 'Large depot with extensive inventory',
    delivery_time_hours: {
      BLUE_LIGHT: 8,
      AIR: 16,
      GROUND: 48
    },
    reliability: 0.99,
    capacity: 'UNLIMITED'
  },
  
  MULTINATIONAL_SUPPLY: {
    id: 'MULTINATIONAL_SUPPLY',
    label: 'Multinational Supply',
    description: 'NATO partner nation support',
    delivery_time_hours: {
      BLUE_LIGHT: 12,
      AIR: 24,
      GROUND: 72
    },
    reliability: 0.85,  // Less reliable due to coordination
    capacity: 'LIMITED'
  },
  
  HOST_NATION: {
    id: 'HOST_NATION',
    label: 'Host Nation Support',
    description: 'Local/host country medical supplies',
    delivery_time_hours: {
      BLUE_LIGHT: 4,
      AIR: 8,
      GROUND: 16
    },
    reliability: 0.80,  // Varies by host nation capability
    capacity: 'LIMITED'
  }
};

/**
 * Get supply source details
 */
export function getSupplySource(sourceId = 'ROLE_3') {
  return SUPPLY_SOURCES[sourceId] || SUPPLY_SOURCES.ROLE_3;
}

/**
 * Calculate estimated delivery time
 */
export function estimateDeliveryTime(sourceId, deliveryMethod) {
  const source = getSupplySource(sourceId);
  return source.delivery_time_hours[deliveryMethod] || 24;
}

// ============================================================================
// INJURY MECHANISM MULTIPLIERS
// ============================================================================

/**
 * Blood consumption multipliers by injury mechanism
 * Base rate: 2.0 PRBC per casualty (NATO average)
 */
export const INJURY_MULTIPLIERS = {
  SMALL_ARMS: {
    id: 'SMALL_ARMS',
    label: 'Small Arms Fire',
    description: 'Penetrating trauma, moderate transfusion need',
    multiplier: 1.5,
    rationale: 'Single-site injuries, localized bleeding'
  },
  
  ARTILLERY: {
    id: 'ARTILLERY',
    label: 'Artillery/Mortars',
    description: 'Fragmentation wounds, high transfusion need',
    multiplier: 2.5,
    rationale: 'Multiple fragmentation wounds, extensive tissue damage'
  },
  
  IED_BLAST: {
    id: 'IED_BLAST',
    label: 'IED/Blast',
    description: 'Polytrauma, massive transfusion protocol',
    multiplier: 3.0,
    rationale: 'Blast injury, traumatic amputation, hemorrhagic shock'
  },
  
  ARMORED_VEHICLE: {
    id: 'ARMORED_VEHICLE',
    label: 'Armored Vehicle Loss',
    description: 'Burns and trauma, high transfusion',
    multiplier: 2.8,
    rationale: 'Burn injuries, blast effects, confined space trauma'
  },
  
  MINE: {
    id: 'MINE',
    label: 'Landmine/AT Mine',
    description: 'Traumatic amputation, massive hemorrhage',
    multiplier: 3.2,
    rationale: 'Lower extremity amputation, massive transfusion often required'
  },
  
  MIXED: {
    id: 'MIXED',
    label: 'Mixed/Standard',
    description: 'Standard NATO distribution of injury mechanisms',
    multiplier: 2.0,
    rationale: 'Weighted average across typical combat injury mix'
  }
};

/**
 * Get injury mechanism multiplier
 */
export function getInjuryMultiplier(mechanismId = 'MIXED') {
  return INJURY_MULTIPLIERS[mechanismId] || INJURY_MULTIPLIERS.MIXED;
}

/**
 * Calculate adjusted consumption rate based on threat profile
 * 
 * @param {number} baseRate - Base consumption rate (default: 2.0 PRBC/casualty)
 * @param {Object} threatProfile - Distribution of injury mechanisms
 * @returns {number} - Adjusted consumption rate
 */
export function calculateAdjustedConsumption(baseRate, threatProfile = {}) {
  // Default threat profile (Ukraine 2022-2024 data)
  const defaultProfile = {
    SMALL_ARMS: 0.25,    // 25% small arms
    ARTILLERY: 0.50,     // 50% artillery/mortars
    IED_BLAST: 0.10,     // 10% IED
    ARMORED_VEHICLE: 0.10, // 10% vehicle
    MINE: 0.05           // 5% mines
  };
  
  const profile = { ...defaultProfile, ...threatProfile };
  
  // Calculate weighted average multiplier
  let weightedMultiplier = 0;
  Object.entries(profile).forEach(([mechanism, percentage]) => {
    const multiplier = INJURY_MULTIPLIERS[mechanism]?.multiplier || 2.0;
    weightedMultiplier += multiplier * percentage;
  });
  
  const adjustedRate = baseRate * weightedMultiplier;
  
  // CL-001: dev-only log removed
  
  return adjustedRate;
}

// ============================================================================
// ASSET STATUS THRESHOLDS (G-05 / C-012 / ARCH-010)
// ============================================================================

/**
 * Four-tier thresholds for MEDEVAC/evacuation asset operational readiness.
 *
 * Used by Tab5 getAssetStatusColor() to color-code capacity utilization
 * and asset readiness percentage displays.
 *
 * ARCH-010 context: CASEVAC tracking in Tab5 is informational only per AJMedP-2.
 * These tiers apply to the MEDEVAC capacity utilization percentage — not to CASEVAC
 * as a DOW-reducing factor (which has been removed in casualtyCalculator.js v5.6.0).
 *
 * Source: NATO medical planning norms for evacuation asset operational readiness.
 * Tiers align with NATO STANAG 2228 asset availability classifications.
 *
 * @property {Object} GREEN  - Adequate readiness (≥60%)
 * @property {Object} YELLOW - Reduced readiness, monitor (40–59%)
 * @property {Object} ORANGE - Critical readiness, priority action (20–39%)
 * @property {Object} RED    - Depleted, MASCAL risk (<20%)
 */
export const ASSET_STATUS_THRESHOLDS = {
  GREEN: {
    min_percent: 60,
    label: 'Adequate',
    mui_color: 'success',
    description: 'Asset readiness within acceptable parameters'
  },
  YELLOW: {
    min_percent: 40,
    label: 'Reduced',
    mui_color: 'warning',
    description: 'Reduced readiness — monitor and consider contingency assets'
  },
  ORANGE: {
    min_percent: 20,
    label: 'Critical',
    mui_color: 'error',
    description: 'Critical readiness — priority action required, request reinforcement'
  },
  RED: {
    min_percent: 0,
    label: 'Depleted',
    mui_color: 'error',
    description: 'Asset capability depleted — MASCAL risk, activate contingency plans'
  }
};

/**
 * Resolve four-tier asset status from a readiness percentage.
 *
 * @param {number} percent - Asset operational readiness (0–100)
 * @returns {{ tier: string, label: string, mui_color: string, description: string }}
 */
export function getAssetStatus(percent) {
  if (percent === null || percent === undefined || isNaN(percent)) {
    return { tier: 'UNKNOWN', label: 'Unknown', mui_color: 'default', description: 'No data available' };
  }
  if (percent >= ASSET_STATUS_THRESHOLDS.GREEN.min_percent) return { tier: 'GREEN',  ...ASSET_STATUS_THRESHOLDS.GREEN  };
  if (percent >= ASSET_STATUS_THRESHOLDS.YELLOW.min_percent) return { tier: 'YELLOW', ...ASSET_STATUS_THRESHOLDS.YELLOW };
  if (percent >= ASSET_STATUS_THRESHOLDS.ORANGE.min_percent) return { tier: 'ORANGE', ...ASSET_STATUS_THRESHOLDS.ORANGE };
  return { tier: 'RED', ...ASSET_STATUS_THRESHOLDS.RED };
}

// ============================================================================
// EXPORTS
// ============================================================================

const resupplyThresholdsAPI = {
  THRESHOLD_PROFILES,
  SUPPLY_SOURCES,
  INJURY_MULTIPLIERS,
  ASSET_STATUS_THRESHOLDS,
  getThresholdProfile,
  getStockoutSeverity,
  getMDOSStatus,
  needsEmergencyResupply,
  getRecommendedResupplyDay,
  getSupplySource,
  estimateDeliveryTime,
  getInjuryMultiplier,
  calculateAdjustedConsumption,
  getAssetStatus
};

export default resupplyThresholdsAPI;
